import { useRef, useState } from 'react';
import { ArrowUpRight, MoveHorizontal, Sparkles } from 'lucide-react';
import { Reveal, Eyebrow } from './Reveal';
import { INSPIRATION_GALLERY, scrollToId } from '../config/site';

const ComparisonCard = ({ item, index, total }) => {
  const [position, setPosition] = useState(50);
  const frameRef = useRef(null);

  const moveTo = (clientX) => {
    const bounds = frameRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width === 0) return;
    const percent = ((clientX - bounds.left) / bounds.width) * 100;
    setPosition(Math.max(0, Math.min(100, Math.round(percent))));
  };

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    moveTo(event.clientX);
  };

  const onPointerMove = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      moveTo(event.clientX);
    }
  };

  const onPointerEnd = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <article
      className="overflow-hidden rounded-[2rem] border border-navy/10 bg-white shadow-lift"
      aria-labelledby={'result-heading-' + item.id}
      data-testid={'result-pair-' + item.id}
    >
      <div className="grid items-stretch lg:grid-cols-[minmax(0,1.05fr)_minmax(0,.95fr)]">
        <div
          ref={frameRef}
          className="ks-real-compare-frame relative isolate aspect-[3/4] w-full cursor-ew-resize select-none overflow-hidden bg-white focus-within:ring-4 focus-within:ring-inset focus-within:ring-aqua"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          role="group"
          aria-label={'Real before-and-after comparison for ' + item.label}
          data-testid={'before-after-comparison-' + item.id}
        >
          <img
            src={item.after}
            alt={item.altAfter}
            draggable="false"
            loading="lazy"
            decoding="async"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
            data-testid={'after-photo-' + item.id}
          />
          <div
            className="pointer-events-none absolute inset-0 overflow-hidden"
            style={{ clipPath: 'inset(0 ' + (100 - position) + '% 0 0)' }}
            aria-hidden="true"
          >
            <img
              src={item.before}
              alt=""
              draggable="false"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover object-center"
              data-testid={'before-photo-' + item.id}
            />
          </div>

          <div
            className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-between gap-3 px-4 sm:top-6 sm:px-6"
            aria-hidden="true"
          >
            <span className="rounded-full bg-navy/90 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.15em] text-white shadow-soft">
              Before
            </span>
            <span className="rounded-full bg-white/95 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.15em] text-navy shadow-soft">
              After
            </span>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-[3px] bg-white shadow-[0_0_0_1px_rgba(11,45,107,.12)]"
            style={{ left: position + '%' }}
            aria-hidden="true"
          >
            <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-[3px] border-white bg-navy text-white shadow-lift sm:h-16 sm:w-16">
              <MoveHorizontal size={27} strokeWidth={2.2} aria-hidden="true" />
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={position}
            onChange={(event) => setPosition(Number(event.target.value))}
            aria-label={'Move the Before / After divider for ' + item.label}
            aria-valuetext={position + '% Before visible'}
            className="sr-only"
            data-testid={'before-after-slider-' + item.id}
          />
        </div>

        <div className="flex min-w-0 flex-col justify-center px-6 py-9 sm:px-10 sm:py-12 lg:px-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-green-tint px-4 py-2 text-xs font-extrabold uppercase tracking-[0.16em] text-navy">
              <Sparkles size={15} aria-hidden="true" /> {item.label}
            </span>
            <span className="text-xs font-extrabold tabular-nums tracking-widest text-charcoal/60">
              {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
          </div>
          <h3
            id={'result-heading-' + item.id}
            className="mt-7 text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl"
          >
            {item.heading}
          </h3>
          <p className="mt-5 text-base leading-relaxed text-charcoal sm:text-lg">
            {item.description}
          </p>
          <p className="mt-7 border-l-4 border-fresh pl-4 text-sm font-bold text-navy">
            {item.accent}
          </p>
          <p className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-navy/80">
            <MoveHorizontal size={19} aria-hidden="true" />
            Drag the center handle to compare. On a keyboard, use the arrow keys.
          </p>
        </div>
      </div>
    </article>
  );
};

const InspirationGallery = () => (
  <section
    id="home-inspiration"
    className="ks-inspiration scroll-mt-24 overflow-hidden py-20 lg:py-28"
    aria-labelledby="inspiration-title"
    data-testid="home-inspiration"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>Real Cleaning Results</Eyebrow>
        <h2
          id="inspiration-title"
          className="mt-4 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl"
        >
          Beautiful <span className="text-fresh">Results.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-charcoal sm:text-lg">
          Real cleaning transformations, captured before and after. Drag the handle to reveal the difference.
        </p>
      </Reveal>

      <div className="mx-auto mt-12 max-w-5xl space-y-10 lg:mt-16 lg:space-y-14">
        {INSPIRATION_GALLERY.map((item, index) => (
          <Reveal key={item.id}>
            <ComparisonCard item={item} index={index} total={INSPIRATION_GALLERY.length} />
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14 text-center">
        <p className="text-base font-semibold text-navy">
          Ready to enjoy a fresher home?
        </p>
        <button
          type="button"
          onClick={() => scrollToId('quote')}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-electric px-9 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-colors hover:bg-navy"
          data-testid="inspiration-quote-button"
        >
          Get a Free Quote
          <ArrowUpRight size={17} aria-hidden="true" />
        </button>
      </Reveal>
    </div>
  </section>
);

export default InspirationGallery;
